import { useT } from "../i18n";
import {
  FONT_NAMES,
  FONT_STACKS,
  READER_FONTS,
  READER_THEMES,
  TEXT_SIZES,
  type ReaderSettings,
  type ReaderTheme,
} from "../player/readerSettings";

/**
 * ReadingSettings is text size, typeface, theme and auto-play, with a paragraph to judge
 * them by. The A buttons step one size; each dot between them jumps to its
 * size.
 *
 * The sample uses the reader's own `reading-text` class and the settings are
 * applied to the document while this is on screen, so what it shows is what a
 * chapter will look like rather than an approximation of it.
 */
export default function ReadingSettings({
  settings,
  onChange,
}: {
  settings: ReaderSettings;
  onChange: (patch: Partial<ReaderSettings>) => void;
}) {
  const t = useT();
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-3 text-xs font-medium text-muted">{t.settings.textSize}</h3>
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label={t.settings.smaller}
            data-testid="text-smaller"
            disabled={settings.size === 0}
            onClick={() => onChange({ size: settings.size - 1 })}
            className="grid size-10 place-items-center rounded-full border border-line text-sm transition-colors hover:bg-sunk disabled:opacity-40"
          >
            A
          </button>
          {/* Every dot is a size you can tap straight to, not just a readout
              between the two buttons. Each is a 32px target around an 8px dot:
              the dot is what the eye reads, the target is what a thumb hits. */}
          <div
            className="flex flex-1 items-center justify-between"
            role="group"
            aria-label={t.settings.textSize}
          >
            {TEXT_SIZES.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={t.settings.sizeOf(i + 1, TEXT_SIZES.length)}
                aria-pressed={i === settings.size}
                data-testid={`text-size-${i}`}
                onClick={() => onChange({ size: i })}
                className="group grid size-8 place-items-center rounded-full"
              >
                <span
                  className={`rounded-full transition-all ${
                    i === settings.size ? "size-3" : "size-2"
                  } ${i <= settings.size ? "bg-sage" : "bg-line-strong group-hover:bg-muted"}`}
                />
              </button>
            ))}
          </div>
          <button
            type="button"
            aria-label={t.settings.larger}
            data-testid="text-larger"
            disabled={settings.size === TEXT_SIZES.length - 1}
            onClick={() => onChange({ size: settings.size + 1 })}
            className="grid size-10 place-items-center rounded-full border border-line text-xl transition-colors hover:bg-sunk disabled:opacity-40"
          >
            A
          </button>
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-xs font-medium text-muted">{t.settings.typeface}</h3>
        {/* Chips, not a menu: four faces fit on a phone's width in two rows,
            and each one is drawn in itself, which is the only way to choose a
            face. A native <select> would hide them behind a tap and draw every
            option in the system's own font. */}
        <div role="group" aria-label={t.settings.typeface} className="flex flex-wrap gap-2">
          {READER_FONTS.map((font) => (
            <button
              key={font}
              type="button"
              aria-pressed={settings.font === font}
              data-testid={`font-${font}`}
              onClick={() => onChange({ font })}
              style={{ fontFamily: FONT_STACKS[font] }}
              className={`rounded-control border px-3 py-2 text-sm transition-colors ${
                settings.font === font
                  ? "border-sage bg-sage-soft text-ink"
                  : "border-line text-ink-soft hover:bg-sunk"
              }`}
            >
              {FONT_NAMES[font]}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[0.7rem] text-muted">{t.settings.typefaceNote}</p>
      </div>

      <div>
        <h3 className="mb-3 text-xs font-medium text-muted">{t.settings.theme}</h3>
        {/* Five now (requirement 0033), so three to a row on a phone rather
            than five squeezed ones. */}
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {READER_THEMES.map((theme) => (
            <button
              key={theme}
              type="button"
              aria-pressed={settings.theme === theme}
              data-testid={`theme-${theme}`}
              onClick={() => onChange({ theme })}
              className={`flex flex-col items-center gap-1.5 rounded-control border px-1 py-2 text-xs transition-colors ${
                settings.theme === theme
                  ? "border-sage text-ink"
                  : "border-line text-muted hover:bg-sunk"
              }`}
            >
              <Swatch theme={theme} />
              {t.settings.themes[theme]}
            </button>
          ))}
        </div>
      </div>

      {/* A plain checkbox: one yes-or-no, and the note says what it does. */}
      <label className="flex cursor-pointer items-start gap-2 text-sm text-ink-soft select-none">
        <input
          type="checkbox"
          data-testid="setting-autoplay"
          checked={settings.autoplay}
          onChange={(e) => onChange({ autoplay: e.target.checked })}
          className="mt-0.5"
        />
        <span>
          {t.settings.autoplay}
          <span className="block text-xs text-muted">{t.settings.autoplayNote}</span>
        </span>
      </label>

      <figure>
        <figcaption className="mb-2 text-xs font-medium text-muted">{t.settings.sample}</figcaption>
        {/* In the interface's language, so the sample is text this reader
            reads. dir="auto" here and on nothing inside it. The size, face and
            palette come from the document root, which `useReaderSettings` is
            already writing to while this page is open — so the sample is the
            setting, not a copy of it. */}
        <p
          dir="auto"
          data-testid="settings-sample"
          className="reading-text rounded-card border border-line bg-surface px-4 py-3"
        >
          {t.settings.sampleText}
        </p>
      </figure>
    </div>
  );
}

/**
 * Swatch previews a theme in its own colours, not the current ones — a preview
 * drawn in tokens would show every theme as whichever is active. "Auto" is
 * half light and half dark, because it is whichever the device says.
 */
function Swatch({ theme }: { theme: ReaderTheme }) {
  const colours: Record<Exclude<ReaderTheme, "auto">, [string, string]> = {
    light: ["#f0efe8", "#1c2118"],
    sepia: ["#f5eddc", "#3a2e20"],
    dark: ["#12160f", "#e8ede2"],
    // Night's own values, from index.css: near-black under warm dim ink. It
    // has to look *quieter* than dark beside it, or the two read as one.
    night: ["#08090a", "#c0b49c"],
  };
  if (theme === "auto") {
    return (
      <span
        aria-hidden="true"
        className="grid size-7 place-items-center rounded-full border border-line-strong"
        style={{ background: "linear-gradient(135deg, #f0efe8 50%, #12160f 50%)" }}
      />
    );
  }
  const [ground, ink] = colours[theme];
  return (
    <span
      aria-hidden="true"
      className="grid size-7 place-items-center rounded-full border border-line-strong text-[0.7rem] font-semibold"
      style={{ background: ground, color: ink }}
    >
      Aa
    </span>
  );
}
